import { isDefined } from '@sama/js-core';
import {
  cloneDeep,
  intersection,
  isEmpty,
  isEqual,
  isUndefined,
  uniqWith,
} from 'lodash';
import { Observable, Subject } from 'rxjs';
import { isShapeLocationExternal } from '../../../../../../cross-team-shared/annotation/shared/state/shape-location-state';
import {
  OrthographicShapeState3D,
  ShapeState,
} from '../../../../../../cross-team-shared/annotation/shared/state/shape-state';
import { objectNumberedKeys } from '../../../../../../cross-team-shared/misc-utils';
import { AssetBoundingBox } from '../../../../../../team-apis/3d/task-data/asset-bounding-box';
import { ShapeGroupOrderState } from '../../../../../../team-apis/content/content-connector';
import { Answer } from '../../../../../../team-apis/otterdam/answers/answer';
import {
  isOrthographicShapeGroupExternal,
  PointCloudAnswer,
  PointCloudOrthographicShapeType,
  PointCloudShapeType,
} from '../../../../../../team-apis/otterdam/answers/point-cloud-answer';
import {
  Point2D,
  Point3D,
  PointType,
  ShapeExternal,
  ShapeGroupExternal,
  ShapeType,
  VectorTaggingShapeType,
} from '../../../../../../team-apis/otterdam/answers/shape-typings';
import { VideoOutput } from '../../../../../../team-apis/otterdam/outputs/video-output';
import { Task } from '../../../../../../team-apis/otterdam/task';
import { TaskWorkspaceStore } from '../../../../../h3ml/state/task-workspace-store';
import { isBlankAnswer } from '../../../utils/misc-utils';
import { FusionMinion } from '../fusion-minion';

export type OrthographicShapeState2D = ShapeState<
  Point2D,
  PointCloudOrthographicShapeType
>;

export const MIN_MAX_MISSING_ERROR =
  'Primary Point Cloud output does not have min/max values. Orthographic task should be re-uploaded with correct metadata';

export class OrthographicOrchestrator {
  private pointCloudBoundingBox!: AssetBoundingBox;
  private initialAnswer?: PointCloudAnswer;
  private name!: string;

  constructor(
    public loadedFrames$: Observable<boolean[]>,
    private answerChanged$: Subject<void>,
    private taskWorkspaceStore: TaskWorkspaceStore,
  ) {}

  setInitialAnswer(answer: PointCloudAnswer | undefined) {
    // TEMPORARY CODE: Remove duplicate points AT-514
    if (!this.taskWorkspaceStore.isUsingObjects() && !isEmpty(answer)) {
      answer.shape_groups.forEach((group) => {
        group.shapes.forEach((shape) => {
          shape.key_locations.forEach((location) => {
            if (isShapeLocationExternal(location)) {
              location.points = uniqWith(location.points, isEqual);
            }
          });
        });
      });
    }
    this.initialAnswer = answer;
  }

  setVideoOutputName(name: string) {
    this.name = name;
  }

  getVideoOutputName() {
    return this.name;
  }

  computeBoundingBoxFromTask(task: Task, primaryPointCloudOutputName: string) {
    try {
      const min: Point3D = JSON.parse(
        task.data[`${primaryPointCloudOutputName} Min`],
      );
      const max: Point3D = JSON.parse(
        task.data[`${primaryPointCloudOutputName} Max`],
      );

      this.pointCloudBoundingBox = { min, max };
    } catch {
      throw new Error(MIN_MAX_MISSING_ERROR);
    }

    return this.pointCloudBoundingBox;
  }

  answersChanged() {
    this.answerChanged$.next();
  }

  getCombinedAnswer(
    pointCloudShapeGroupOrder: ShapeGroupOrderState,
    videoShapeGroupOrder: ShapeGroupOrderState,
  ) {
    const combinedShapeGroupOrder = cloneDeep(pointCloudShapeGroupOrder);

    if (isUndefined(videoShapeGroupOrder)) {
      return combinedShapeGroupOrder;
    }

    this.convertAndAppendShapeGroups(
      combinedShapeGroupOrder,
      videoShapeGroupOrder,
    );

    return combinedShapeGroupOrder;
  }

  getVideoAnswerPortion() {
    if (isBlankAnswer(this.initialAnswer)) {
      return {};
    }

    return {
      ...this.initialAnswer,
      shape_groups: this.convertTo2DShapeGroups(
        this.initialAnswer.shape_groups.filter(
          isOrthographicShapeGroupExternal,
        ),
      ),
    };
  }

  getPointCloudAnswerPortion() {
    if (isBlankAnswer(this.initialAnswer)) {
      return {};
    }
    return {
      ...this.initialAnswer,
      shape_groups: this.initialAnswer.shape_groups
        .filter(isOrthographicShapeGroupExternal)
        .map((group) => ({
          group_type: group.group_type,
          shapes: group.shapes,
        })),
    };
  }

  convertShapeToPointCloudCoordinates(shape: OrthographicShapeState2D) {
    const shape3D: OrthographicShapeState3D = {
      ...shape,
      key_locations: shape.key_locations.map((keyLocation) => ({
        ...keyLocation,
        points: keyLocation.points.map(convertToPointCloudCoordinates),
        // We are not able to support ml_extrapolations in orthographic shapes
        // without further changes to 3d and draft saves
        model_input_points: undefined,
        extrapolation: undefined,
      })),
    };
    return shape3D;
  }

  private convertTo2DShapeGroups(
    pointCloudShapeGroups: Array<
      ShapeGroupExternal<Point3D, VectorTaggingShapeType>
    >,
  ) {
    return pointCloudShapeGroups.map((group) => ({
      group_type: group.group_type,
      shapes: group.shapes.map((pointCloudShape) =>
        mapShapePoints<Point3D, Point2D, VectorTaggingShapeType>(
          pointCloudShape,
          convertToVideoCoordinatesObjectless,
        ),
      ),
    }));
  }

  private convertAndAppendShapeGroups(
    combinedShapeGroupOrder: ShapeGroupOrderState,
    videoShapeGroupOrder: ShapeGroupOrderState,
  ) {
    videoShapeGroupOrder.shapeAndGroupOrder.forEach((shapeOrGroupOrder) => {
      const index = shapeOrGroupOrder.index;

      if (
        shapeOrGroupOrder.type === 'group' &&
        isDefined(videoShapeGroupOrder.groups[index])
      ) {
        const shapeIndexes = intersection(
          objectNumberedKeys(combinedShapeGroupOrder.shapes),
          // @ts-ignore -- fix me
          videoShapeGroupOrder.groups[index].shapeIndexes,
        );
        if (isEmpty(shapeIndexes)) {
          return;
        }
        // @ts-ignore -- fix me
        combinedShapeGroupOrder.groups[index] = {
          ...videoShapeGroupOrder.groups[index],
          shapeIndexes,
        };
      } else if (isUndefined(combinedShapeGroupOrder.shapes[index])) {
        return;
      }
      combinedShapeGroupOrder.shapeAndGroupOrder.push(shapeOrGroupOrder);
    });
  }
}

function mapShapePoints<
  InputPointType extends PointType,
  OutputPointType extends PointType,
  InputShapeType extends ShapeType = ShapeType,
>(
  shape: ShapeExternal<InputPointType, InputShapeType>,
  conversionFunction: (point: InputPointType) => OutputPointType,
) {
  return {
    ...shape,
    key_locations: shape.key_locations.map((keyLocation) => {
      if (!isShapeLocationExternal(keyLocation)) {
        return keyLocation;
      }

      return {
        ...keyLocation,
        model_input_points: undefined,
        points: keyLocation.points.map(conversionFunction),
      };
    }),
  };
}

export function shouldRenderOrthographic(
  primaryMinion: FusionMinion,
  videoOutput: VideoOutput,
) {
  return (
    primaryMinion.output.type === 'PointCloudOutput' &&
    // Can be true even if the video is not in a sensor fusion or the
    // primary output is not point-cloud, so we need to check both
    videoOutput.meta.is_orthographic_projection
  );
}

/** @deprecated used for old projects without objects */
export function filterOutOrthographicShapes(answer: Answer) {
  if (isEmpty(answer) || !answer.shape_groups) {
    return answer;
  }
  return {
    ...answer,
    shape_groups: answer.shape_groups.filter(
      (
        group: ShapeGroupExternal<
          Point3D,
          PointCloudShapeType | VectorTaggingShapeType
        >,
      ) => !isOrthographicShapeGroupExternal(group),
    ),
  };
}

export function convertToPointCloudCoordinates(point: Point2D): Point3D {
  // In SVG, negative Y goes up, but in 3D, negative Y goes down
  return [point[0], -point[1], NaN];
}

export function convertToVideoCoordinatesObjectless(point: Point3D): Point2D {
  // In SVG, negative Y goes up, but in 3D, negative Y goes down
  return [point[0], -point[1]];
}

export function flipYCoordinate(point: PointType): PointType {
  const convertedPoint: PointType = [...point];
  // In SVG, negative Y goes up, but in 3D, negative Y goes down
  convertedPoint[1] = -convertedPoint[1];
  return convertedPoint;
}
